import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Tab,
  CardActions,
  Chip,
  IconButton,
  Tooltip,
  Pagination,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  AssignmentOutlined,
  ArrowBack,
  School,
  AccessTime,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { learningPathsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import ConfirmDialog from '../components/ConfirmDialog';
import Card from '../components/UIKit/Card';
import Tabs from '../components/UIKit/Tabs';
import Button from '../components/UIKit/Button';

// Constants
const MAX_VISIBLE_PROFESSIONS = 2;

// Функция для определения цвета сложности (не зависит от состояния)
const getDifficultyColor = (level) => {
  switch (level?.toLowerCase()) {
    case 'начинающий':
    case 'beginner':
      return 'success';
    case 'средний':
    case 'intermediate':
      return 'warning';
    case 'продвинутый':
    case 'advanced':
      return 'error';
    default:
      return 'default';
  }
};

const MyLearningPaths = () => {
  const { t } = useTranslation(['common', 'learningPaths']);
  const [tabValue, setTabValue] = useState(0);
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  // Диалог подтверждения удаления
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pathToDelete, setPathToDelete] = useState(null);

  const fetchMyPaths = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Параметры пагинации
      const params = {
        page,
        limit: itemsPerPage,
      };

      let response;
      if (tabValue === 0) {
        // Все мои дорожки
        response = await learningPathsAPI.getMy(params);
      } else if (tabValue === 1) {
        // Черновики
        response = await learningPathsAPI.getMyDrafts(params);
      } else if (tabValue === 2) {
        // Опубликованные
        response = await learningPathsAPI.getMyPublished(params);
      }

      // Обработка ответа с новой структурой
      let pathsData = [];

      if (response && response.data) {
        // Новая структура ответа
        pathsData = Array.isArray(response.data.data) ? response.data.data : [];

        const paginationInfo = response.data.pagination || {
          total: 0,
          pages: 1,
        };
        setTotalPages(paginationInfo.pages);
      } else if (Array.isArray(response)) {
        // Старая структура - прямой массив
        pathsData = response;
        setTotalPages(Math.ceil(response.length / itemsPerPage));
      }

      setPaths(pathsData);
      setLoading(false);
    } catch (err) {
      console.error(t('learningPaths:error.load'), err);
      setError(t('learningPaths:error.load'));
      setLoading(false);
    }
  }, [tabValue, page, itemsPerPage, t]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchMyPaths();
  }, [isAuthenticated, navigate, fetchMyPaths]);

  const handleTabChange = useCallback((event, newValue) => {
    setTabValue(newValue);
    setPage(1); // Сбрасываем страницу при смене вкладки
  }, []);

  const handlePageChange = useCallback((event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleDeleteClick = useCallback((path) => {
    setPathToDelete(path);
    setConfirmOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!pathToDelete) return;

    try {
      await learningPathsAPI.delete(pathToDelete._id);
      setPaths(paths.filter((path) => path._id !== pathToDelete._id));
      toast.success(t('learningPaths:success.deleted'));
    } catch (err) {
      console.error(t('learningPaths:error.delete'), err);
      toast.error(t('learningPaths:error.delete'));
    } finally {
      setConfirmOpen(false);
      setPathToDelete(null);
    }
  }, [pathToDelete, paths, t]);

  // Функция для получения имени профессии
  const getProfessionName = useCallback((profession) => {
    if (!profession) return t('common:status.noProfession');

    if (typeof profession === 'string') return profession;

    if (profession.term_group && profession.term_group.MainTerm && profession.term_group.MainTerm.value) {
      return profession.term_group.MainTerm.value;
    }

    return profession.name || t('common:status.noProfession');
  }, [t]);

  // Функция для форматирования времени в часы и минуты
  const formatTimeHoursMinutes = useCallback((minutes) => {
    if (!minutes || minutes <= 0) return null;

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0 && remainingMinutes > 0) {
      return `${hours} ${t('common:time.shortHour')} ${remainingMinutes} ${t(
        'common:time.shortMin'
      )}`;
    } else if (hours > 0) {
      return `${hours} ${t('common:time.shortHour')}`;
    } else {
      return `${remainingMinutes} ${t('common:time.shortMin')}`;
    }
  }, [t]);

  // Рендер чипов профессий с индикатором переполнения
  const renderProfessionChips = useCallback((professions) => {
    if (
      !professions ||
      !Array.isArray(professions) ||
      professions.length === 0
    ) {
      return null;
    }

    // Фильтруем null и undefined значения
    const validProfessions = professions.filter(
      (prof) => prof !== null && prof !== undefined
    );

    if (validProfessions.length === 0) {
      return null;
    }

    // Показываем только ограниченное количество профессий
    const visibleProfessions = validProfessions.slice(
      0,
      MAX_VISIBLE_PROFESSIONS
    );
    const remainingCount = validProfessions.length - MAX_VISIBLE_PROFESSIONS;

    return (
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'nowrap',
          overflow: 'hidden',
          my: 1,
        }}
      >
        {visibleProfessions.map((profession, index) => (
          <Tooltip
            title={getProfessionName(profession)}
            key={
              typeof profession === 'object'
                ? profession.id || profession._id || index
                : profession || index
            }
            placement="top"
          >
            <Chip
              label={getProfessionName(profession)}
              size="small"
              color="primary"
              sx={{
                maxWidth: 'calc(50% - 8px)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            />
          </Tooltip>
        ))}

        {remainingCount > 0 && (
          <Tooltip
            title={validProfessions
              .slice(MAX_VISIBLE_PROFESSIONS)
              .map((p) => getProfessionName(p))
              .join(', ')}
            placement="top"
          >
            <Chip label={`+${remainingCount}`} size="small" color="primary" />
          </Tooltip>
        )}
      </Box>
    );
  }, [getProfessionName]);

  return (
    <Container sx={{ py: 4 }}>
      <Box
        mb={4}
        display="flex"
        flexWrap="wrap"
        gap={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            component={Link}
            to="/learning-paths"
            sx={{ mr: 3 }}
          >
            {t('common:actions.back')}
          </Button>
          <Box display="flex" alignItems="center">
            <AssignmentOutlined
              fontSize="large"
              color="primary"
              sx={{ mr: 2 }}
            />
            <Typography variant="h3" component="h1" fontWeight="bold">
              {t('learningPaths:myPaths')}
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          component={Link}
          sx={{
            width: { xs: '100%', sm: 'auto' },
          }}
          to="/learning-paths/create"
        >
          {t('learningPaths:create')}
        </Button>
      </Box>

      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: { xs: '12px', sm: '16px' },
          p: '4px',
          width: '100%',
          overflow: 'hidden',
          mb: 3,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          swapBackground
        >
          <Tab label={t('learningPaths:allPaths')} />
          <Tab label={t('learningPaths:drafts')} />
          <Tab label={t('learningPaths:published')} />
        </Tabs>
      </Box>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="400px"
        >
          <CircularProgress />
        </Box>
      ) : error ? (
        <>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button variant="contained" color="primary" onClick={fetchMyPaths}>
            {t('common:actions.retry')}
          </Button>
        </>
      ) : !Array.isArray(paths) || paths.length === 0 ? (
        <Alert severity="info" sx={{ mb: 4 }}>
          {tabValue === 0
            ? t('learningPaths:noPaths')
            : tabValue === 1
            ? t('learningPaths:noDrafts')
            : t('learningPaths:noPublished')}
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {Array.isArray(paths) &&
            paths.map((path) => (
              <Grid item xs={12} md={6} xl={4} xxl={3} key={path._id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    overflow: 'hidden',
                    borderRadius: '12px',
                  }}
                >
                  <Box
                    sx={{
                      height: '160px',
                      backgroundImage: `url(${
                        path.image || '/images/default-path.jpg'
                      })`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      position: 'relative',
                      borderRadius: '8px',
                    }}
                  >
                    {path.difficulty && (
                      <Box position="absolute" top="12px" right="12px">
                        <Chip
                          size="small"
                          label={path.difficulty}
                          color={getDifficultyColor(path.difficulty)}
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                    )}

                    {path.isPublished === false && (
                      <Box position="absolute" top="12px" left="12px">
                        <Chip
                          size="small"
                          label={t('common:status.draft')}
                          color="primary"
                          variant="filled"
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                    )}
                  </Box>

                  <Box
                    sx={{
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1
                    }}
                  >
                    <Typography
                      variant="h6"
                      component="h2"
                      sx={{
                        fontWeight: 700,
                        fontSize: '1.2rem',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        height: '60px',
                      }}
                    >
                      {path.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        height: '70px',
                      }}
                    >
                      {path.description}
                    </Typography>

                    {/* Отображение профессий */}
                    <Box>{renderProfessionChips(path.professions)}</Box>

                    {path.tags && path.tags.length > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '8px',
                        }}
                      >
                        {path.tags.slice(0, 3).map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                        {path.tags.length > 3 && (
                          <Chip
                            label={`+${path.tags.length - 3}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    )}

                    <Box sx={{ flexGrow: 1 }} />

                    {(path.courses?.length > 0 ||
                      path.estimatedTimeMinutes > 0) && (
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                        }}
                      >
                        {path.courses?.length > 0 && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '0.85rem',
                              color: 'text.secondary',
                            }}
                          >
                            <School fontSize="small" />
                            <Typography variant="body2">
                              {path.courses.length} {t('common:stats.lessons')}
                            </Typography>
                          </Box>
                        )}

                        {path.estimatedTimeMinutes > 0 && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '0.85rem',
                              color: 'text.secondary',
                            }}
                          >
                            <AccessTime fontSize="small" />
                            <Typography variant="body2">
                              {formatTimeHoursMinutes(
                                path.estimatedTimeMinutes
                              )}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}

                    <CardActions
                      sx={{
                        p: 0,
                      }}
                    >
                      <Tooltip title={t('common:actions.view')}>
                        <IconButton
                          component={Link}
                          to={`/learning-paths/${path._id}`}
                          color="primary"
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common:actions.edit')}>
                        <IconButton
                          component={Link}
                          to={`/learning-paths/${path._id}/edit`}
                          color="primary"
                          disabled={!user?.roles?.includes('admin') && path.isPublished}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common:actions.delete')}>
                        <IconButton
                          onClick={() => handleDeleteClick(path)}
                          color="error"
                          disabled={!user?.roles?.includes('admin') && path.isPublished}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </CardActions>
                  </Box>
                </Card>
              </Grid>
            ))}
        </Grid>
      )}

      {!loading && !error && totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="medium"
          />
        </Box>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title={t('learningPaths:delete')}
        content={t('learningPaths:deleteConfirmation')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </Container>
  );
};

export default MyLearningPaths;
